import type { IntegrationDefinition, PlatformUrn } from './types.js';

export interface IntegrationRegistry {
  get(urn: string): IntegrationDefinition | undefined;
  getOrThrow(urn: string): IntegrationDefinition;
  has(urn: string): boolean;
  list(): readonly IntegrationDefinition[];
  listByCapability(capability: 'connection'): readonly IntegrationDefinition[];
}

/** Aggregates integration definitions into one lookup keyed by URN. Throws on duplicate URNs at construction. */
export function createIntegrationRegistry(
  definitions: readonly IntegrationDefinition[],
): IntegrationRegistry {
  const byUrn = new Map<PlatformUrn, IntegrationDefinition>();

  for (const definition of definitions) {
    if (byUrn.has(definition.meta.urn)) {
      throw new Error(
        `Duplicate integration URN registered: ${definition.meta.urn}`,
      );
    }
    byUrn.set(definition.meta.urn, definition);
  }

  const list = (): readonly IntegrationDefinition[] =>
    Array.from(byUrn.values());

  return {
    get: (urn) => byUrn.get(urn as PlatformUrn),
    getOrThrow: (urn) => {
      const definition = byUrn.get(urn as PlatformUrn);
      if (!definition) {
        throw new Error(`No integration registered for URN: ${urn}`);
      }
      return definition;
    },
    has: (urn) => byUrn.has(urn as PlatformUrn),
    list,
    listByCapability: (capability) =>
      list().filter((definition) => Boolean(definition[capability])),
  };
}
